//@author: devin
import { NgTemplateOutlet } from "@angular/common";
import {
    booleanAttribute,
    Component,
    computed,
    DestroyRef,
    DOCUMENT,
    effect,
    ElementRef,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    TemplateRef,
    untracked,
    viewChild,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from "@angular/core";
import { NgbPopover, Placement, PlacementArray } from "@ng-bootstrap/ng-bootstrap";
import { fromEvent, merge } from "rxjs";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AngBridgeUtil } from "../../util/ang.bridge.util";

@TypeManagerDecorator("s25-popover")
@Component({
    selector: "s25-popover",
    host: {
        "[class.ngInlineBlock]": "inline()",
        "(mouseleave)": "catchCloseEvent($event)",
        "(focusout)": "catchCloseEvent($event)",
        "(mouseenter)": "catchOpenEvent($event)",
        "(click)": "onHostClick($event)",
    },
    template: `
        @if (disabled()) {
            <ng-container [ngTemplateOutlet]="content"></ng-container>
        } @else {
            <div
                #po="ngbPopover"
                [popoverClass]="popoverClass()"
                [ngbPopover]="popoverTemplate() ?? modelBean()?.popoverTemplate"
                [popoverContext]="popoverContext()"
                [autoClose]="'outside'"
                [triggers]="ngbTriggers()"
                (shown)="currentState.set('open')"
                (hidden)="currentState.set('closed')"
                (mouseenter)="catchOpenEvent($event)"
                (focusin)="catchOpenEvent($event)"
                (keydown.enter)="catchOpenEvent($event)"
                [container]="onBody() ? 'body' : null"
                [placement]="placement()"
                [attr.tabindex]="triggerTabIndex()"
            >
                <ng-container [ngTemplateOutlet]="content"></ng-container>
            </div>
        }
        <ng-template #content>
            <ng-content></ng-content>
        </ng-template>
    `,
    styles: `
        div {
            height: 100%;
        }
        ::ng-deep .bs-popover-bottom .popover-arrow {
            --bs-popover-bg: var(--color--popover-highlight, var(--color--surface-bg));
            --bs-popover-arrow-border: var(--color--popover-highlight, var(--color--border));
        }
    `,
    standalone: true,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgbPopover, NgTemplateOutlet],
})
export class PopoverComponent<TContext = unknown> {
    public static readonly DEFAULT_CLOSE_DELAY_MS: number = 600;
    public static readonly DEFAULT_OPEN_DELAY_MS: number = 500;

    public static sanitizePlacement(value?: PlacementArray): Placement[] {
        if (!value) return ["auto"];
        const asArray = !Array.isArray(value) ? value.split(" ") : value;
        return asArray.filter((p): p is Placement => validPlacements.has(p));
    }

    /** @deprecated use granular inputs instead */
    readonly modelBean = input<PopoverModelBean<TContext>>();

    readonly popoverTemplate = input.required<string | TemplateRef<TContext>>();
    readonly popoverContext = input<TContext>();
    readonly isTooltip = input<boolean>(false);
    //default to popover on body https://ng-bootstrap.github.io/#/guides/positioning
    readonly onBody = input<boolean>(true);
    readonly openTrigger = input<"mouseenter" | "click">("mouseenter");
    readonly closeTrigger = input<"mouseleave" | "click">("mouseleave");
    /** Defaults to 0 for click-triggered popovers, otherwise {@link PopoverComponent.DEFAULT_OPEN_DELAY_MS} */
    readonly openDelayMillis = input<number>();
    /** Defaults to 0 for click-triggered popovers, otherwise {@link PopoverComponent.DEFAULT_CLOSE_DELAY_MS} */
    readonly closeDelayMillis = input<number>();
    /**
     * Set to true to prevent pop-overs
     */
    readonly disabled = input(false, { transform: booleanAttribute });
    readonly inline = input<boolean>(true);
    readonly placement = input(["auto"] satisfies Placement[], { transform: PopoverComponent.sanitizePlacement });
    /**
     * Additional CSS class(es) to apply to the popover
     */
    readonly popoverClassInput = input<string | undefined>(undefined, { alias: "popoverClass" });
    /**
     * Tab index on the popover trigger wrapper. Use -1 when projected content is already
     * focusable (e.g. a button) to avoid duplicate tab stops.
     */
    readonly triggerTabIndex = input<number>(0);
    readonly shown = output<void>();
    readonly hidden = output<void>();
    readonly initialized = output<boolean>();

    readonly elementRef = inject(ElementRef);
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly document = inject(DOCUMENT);

    protected readonly popoverOutlet = viewChild(NgbPopover);

    readonly uuid = "s25Popover-" + Date.now() + "-" + Math.floor(Math.random() * 10000);
    protected readonly popoverClass = computed(
        () => `${this.popoverClassInput() ?? this.modelBean()?.popoverClass ?? ""} ${this.uuid}`,
    );
    /** Click-triggered popovers open/close only through this component, so ngbPopover's own click toggle must be off */
    protected readonly ngbTriggers = computed(() => (this.openTrigger() === "click" ? "manual" : "click"));
    protected readonly openDelay = computed(
        () => this.openDelayMillis() ?? (this.openTrigger() === "click" ? 0 : PopoverComponent.DEFAULT_OPEN_DELAY_MS),
    );
    protected readonly closeDelay = computed(
        () =>
            this.closeDelayMillis() ?? (this.closeTrigger() === "click" ? 0 : PopoverComponent.DEFAULT_CLOSE_DELAY_MS),
    );
    readonly popoverElement = signal<Element | undefined>(undefined);
    protected readonly prevFocusedElement = signal<HTMLElement | undefined>(undefined);
    #resetIgnoreOpenEventTimeout?: ReturnType<typeof setTimeout>;

    /**
     * If this is "open", then the popover is definitely open and visible to the user.
     * Otherwise, the popover is definitely closed and not visible.
     */
    protected readonly currentState = signal<"open" | "closed">("closed");
    /**
     * Setting this will eventually update the state with a configurable delay. This is useful
     * for debouncing changes to the state to reduce flicker.
     */
    protected readonly desiredState = linkedSignal({
        source: () => this.currentState,
        computation: (state) => state(),
    });
    // whether we should try to autofocus content when the popover opens
    protected readonly shouldFocusContent = signal<boolean>(false);

    /** when returning focus to the previous element, ignore the open event that may be triggered */
    #ignoreOpenEvent = false;

    constructor() {
        AngBridgeUtil.init(this);
        this.openOrCloseWithDelay();
        this.handleOpenAndClose();
        this.catchPopoverEnterExitEvents();
        this.closeWhenDisabled();
        this.destroyRef.onDestroy(() => clearTimeout(this.#resetIgnoreOpenEventTimeout));
    }

    /**
     * Suppresses the next open caused by a "focusin" bubbling out of this popover's trigger/content
     * (e.g. a caller focusing the trigger or a projected element without wanting that to activate
     * this popover). Has no effect on open triggers other than "focusin".
     */
    ignoreNextOpenEvent() {
        clearTimeout(this.#resetIgnoreOpenEventTimeout);
        this.#ignoreOpenEvent = true;
        this.#resetIgnoreOpenEventTimeout = setTimeout(() => (this.#ignoreOpenEvent = false), 0);
    }

    private openOrCloseWithDelay() {
        effect((onCleanup) => {
            if (this.desiredState() === this.currentState()) {
                // no action necessary
                return;
            } else if (this.desiredState() === "closed") {
                this.shouldFocusContent.set(false);
                const delayedClose = setTimeout(() => this.forceClose(), this.closeDelay());
                onCleanup(() => clearTimeout(delayedClose));
            } else if (this.desiredState() === "open" && !this.disabled()) {
                const delayedOpen = setTimeout(() => this.popoverOutlet().open(), this.openDelay());
                onCleanup(() => clearTimeout(delayedOpen));
            }
        });
    }

    /**
     * Focuses the first tabbable element in `popoverContent` (TODO: allow external components to
     * define this focus target behavior), without opening any popover that element triggers
     * (e.g. an `<s25-item>` tooltip).
     */
    focusFirstContentElement(popoverContent: Element) {
        const newFocusedElement = popoverContent.querySelector<HTMLElement>(`input, [tabindex="0"]`);
        if (!newFocusedElement) return;

        this.prevFocusedElement.set(document.activeElement as HTMLElement);
        this.focusElement(newFocusedElement);
    }

    private focusElement(element: HTMLElement, options?: { preventScroll?: boolean }) {
        if (!element) return;
        const itemBridge = <PopoverComponent | undefined>element.closest<HTMLElement>("s25-popover")?.angBridge;
        itemBridge?.ignoreNextOpenEvent();
        element.focus({ preventScroll: options?.preventScroll ?? false });
    }

    private handleOpenAndClose() {
        effect((onCleanup) => {
            if (this.currentState() === "open") {
                const popoverContent = document.querySelector("." + this.uuid);
                this.popoverElement.set(popoverContent);
                if (untracked(() => this.shouldFocusContent())) {
                    this.focusFirstContentElement(popoverContent);
                }
                this.shown.emit();
                this.initialized.emit(true);
                onCleanup(() => {
                    if (this.currentState() === "closed" && !this.destroyRef.destroyed) {
                        this.hidden.emit();
                    }
                });
            } else if (this.currentState() === "closed") {
                this.shouldFocusContent.set(false);
                this.popoverElement.set(undefined);
                const prevFocusedElement = this.prevFocusedElement();
                if (prevFocusedElement) {
                    this.prevFocusedElement.set(undefined);
                    // Don't do anything if focus is already somewhere else
                    if (this.document.activeElement && this.document.activeElement !== this.document.body) {
                        return;
                    }
                    this.focusElement(prevFocusedElement, { preventScroll: true });
                }
            }
        });
    }

    private catchPopoverEnterExitEvents() {
        effect((onCleanup) => {
            const popoverContent = this.popoverElement();
            if (popoverContent) {
                // if the user exits the host element, desired state will be set to "closed", but they may
                // then enter the popover. in this case, we need to update the desired state back to "open"
                const keepOpenOnInteract = merge(
                    fromEvent(popoverContent, "mouseenter"),
                    fromEvent(popoverContent, "click"),
                    fromEvent(popoverContent, "focusin"),
                ).subscribe(() => this.desiredState.set("open"));
                onCleanup(() => keepOpenOnInteract.unsubscribe());

                const closeOnLeave = fromEvent(popoverContent, "mouseleave").subscribe((event: UIEvent) =>
                    this.catchCloseEvent(event),
                );
                onCleanup(() => closeOnLeave.unsubscribe());
            }
        });
    }

    private closeWhenDisabled() {
        effect(() => {
            if (this.disabled()) {
                this.forceClose();
            }
        });
    }

    protected onHostClick($event: MouseEvent) {
        // When onBody() is false, the popover panel is a DOM descendant of this host (not
        // portaled to body), so clicks inside the open content (e.g. a filter input) bubble
        // up here too. Those must not count as "re-click the trigger to close".
        if (this.popoverElement()?.contains($event.target as Node)) {
            this.catchOpenEvent($event);
            return;
        }
        if (this.closeTrigger() === "click" && this.currentState() === "open") {
            this.desiredState.set("closed");
            return;
        }
        this.catchOpenEvent($event);
    }

    protected catchOpenEvent($event: Event) {
        if (this.#ignoreOpenEvent) {
            return;
        }
        if (!this.disabled() && this.shouldEventTriggerOpen($event)) {
            this.desiredState.set("open");
            if (
                this.currentState() !== "open" &&
                $event.type !== "mouseenter" &&
                this.elementRef.nativeElement.contains($event.target)
            ) {
                this.shouldFocusContent.set(true);
            }
        }
    }

    private shouldEventTriggerOpen($event: any) {
        return (
            $event.type === this.openTrigger() ||
            ($event.type === "focusin" && this.isTooltip()) ||
            (this.openTrigger() === "click" && $event.key === "Enter")
        );
    }

    protected catchCloseEvent($event: UIEvent) {
        if ($event.type === this.closeTrigger()) {
            this.desiredState.set("closed");
            return;
        }
        if ($event.type === "focusout" && this.isTooltip()) {
            const relatedTarget = ($event as FocusEvent).relatedTarget as Node | null;
            if (
                relatedTarget &&
                (this.elementRef.nativeElement.contains(relatedTarget) ||
                    this.popoverElement()?.contains(relatedTarget))
            ) {
                return;
            }
            this.desiredState.set("closed");
        }
    }

    forceOpen() {
        this.desiredState.set("open");
        this.shouldFocusContent.set(true);
        this.popoverOutlet()?.open?.();
    }

    forceClose() {
        this.desiredState.set("closed");
        this.popoverOutlet()?.close?.();
    }
}

const validPlacements = new Set<string>([
    "auto",
    "top",
    "top-start",
    "top-left",
    "top-end",
    "top-right",
    "bottom",
    "bottom-start",
    "bottom-left",
    "bottom-end",
    "bottom-right",
    "start",
    "start-top",
    "start-bottom",
    "left",
    "left-top",
    "left-bottom",
    "end",
    "end-top",
    "end-bottom",
    "right",
    "right-top",
    "right-bottom",
] satisfies Placement[]);

// still used in s25.multiselect, s25.md.tag.picker, s25.generic.dropdown and a couple others
export type PopoverModelBean<TContext = unknown> = {
    readonly popoverTemplate?: string | TemplateRef<TContext>;
    readonly popoverClass?: string;
};
